import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import { computed, inject } from '@angular/core';
import { EnvironmentService, LoggingService } from '@mwe/services';
import { CookieFormContents, CookieState, DeepPartial } from '@mwe/models';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { fromFetch } from 'rxjs/internal/observable/dom/fetch';
import { catchError, of, pipe, switchMap, tap } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { track } from '@mwe/utils';

const NAME_SPACE = 'we.cookie-consent';

type CookieData = { days?: number; data: string };

export const CookieStore = signalStore(
  { providedIn: 'root' },
  withState<CookieState>({
    acceptedVersion: null,
    saved: null,
    data: null,
    categories: null,
  }),
  withComputed(store => {
    const dependencies = computed(() => {
      return store?.data()?.data?.dependencies ?? [];
    });

    const groups = computed(() => {
      const services = store?.data()?.data?.services ?? [];
      const groups =
        services?.map(item => ({
          ...item,
          services: item.services.map(service => ({
            ...service,
            dependencies: service.dependencies && service.dependencies.map(dependency => dependencies().find(dep => dep.id === dependency)),
          })),
        })) ?? [];

      return groups.map(group => ({
        ...group,
        services: group?.services?.map(service => ({
          ...service,
          accepted: store.saved()?.includes(service?.id),
        })),
      }));
    });

    const version = computed(() => {
      return store?.data()?.meta?.version ?? '';
    });

    const acceptAll = computed(() => {
      const ids = new Set<string>();

      groups().forEach(group => {
        group.services.forEach(service => {
          ids.add(service.id);
          service.dependencies.forEach(dependency => {
            ids.add(dependency.id);
          });
        });
      });

      return ids;
    });

    return {
      dependencies,
      groups,
      version,
      acceptAll,
    };
  }),
  withMethods(store => {
    const environmentService = inject(EnvironmentService);
    const loggingService = inject(LoggingService);

    // Base Cookie Logic
    const getCookies = () => {
      return Cookies.get(NAME_SPACE);
    };

    const setCookie = ({ days = 30, data }: CookieData) => {
      const expires = dayjs().add(days, 'day').toDate();

      const host = window.location.host;
      const domain = host.includes('localhost') ? 'localhost' : `.${host.split('.').slice(-2).join('.')}`;

      return Cookies.set(NAME_SPACE, `${store.version()}::${data ?? ''}`, {
        expires,
        domain,
      });
    };

    // Cookie Data Fetching
    const getCookieData = rxMethod<void>(
      pipe(
        switchMap(() => fromFetch(`${environmentService.staticContent()}/modules/cookie-consent/cookie-consent-data.json`)),
        switchMap((response: Response) => {
          if (response.ok) {
            return fromPromise(response.json());
          }
          return of({ error: true, message: `Error ${response.status}` });
        }),
        tap(data => {
          if (!data.error) {
            patchState(store, { data });
          }
        }),
        catchError(err => {
          loggingService.logError(new Error('Cookie Consent Loading Error'), err.message);
          return of({ error: true, message: err.message });
        }),
      ),
    );

    // Cookie Logic
    const getSavedConsent = () => {
      const cookie = getCookies();
      if (!cookie?.length) return [];

      const [version = null, consents = null] = cookie.split('::') || [];
      if (!version) {
        return [];
      }

      patchState(store, {
        acceptedVersion: version,
        saved: consents?.split(',') || [],
      });

      return consents?.split(',');
    };

    const hasPartialConsent = (cookies: DeepPartial<CookieFormContents>[]) => {
      if (!cookies) return null;
      const ids = new Set<string>();

      cookies.forEach(group => {
        group.services.forEach(service => {
          ids.add(service.id);
          service.dependencies?.forEach(dependency => {
            ids.add(dependency);
          });
        });
      });

      const totalExpectedConsents = ids.size;
      const partialConsent = store.acceptAll()?.size !== totalExpectedConsents;
      return {
        partialConsent,
        data: partialConsent ? [...ids]?.join(',') : [...store.acceptAll()]?.join(','),
      };
    };

    const saveCookie = ({ cookies, acceptAll }: { cookies: DeepPartial<CookieFormContents>[]; acceptAll: boolean }) => {
      const { partialConsent, data } = hasPartialConsent(cookies);

      const cookie: CookieData = {
        days: data?.length ? (partialConsent ? 182 : 365) : 30,
        data,
      };

      setCookie(cookie);
      getSavedConsent();

      // Tracking Stuff
      track(acceptAll ? 'all' : 'partial', {
        type: 'cookie-consent',
        services: data?.split(','),
        version: store.version(),
        title: document.title,
        url: document.location.href,
      });

      if (acceptAll) {
        // @ts-ignore
        window?.ppms?.cm?.api('trackAgreeToAllClick');
      } else {
        // @ts-ignore
        data?.length ? window?.ppms?.cm?.api('trackSaveChoicesClick') : window?.ppms?.cm?.api('trackRejectAllClick');
      }
    };

    return {
      getCookieData,
      getSavedConsent,
      saveCookie,
    };
  }),
  withHooks(store => {
    const onInit = () => {
      store.getCookieData();
      store.getSavedConsent();

      track('initial', {
        services: store.saved() ?? [''],
        type: 'cookie-consent',
      });
    };

    return {
      onInit,
    };
  }),
);
